import { writeFile, mkdir, readFile } from 'node:fs/promises'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { execSync } from 'node:child_process'
import { randomBytes } from 'node:crypto'
import { TontyError, SetupError } from '../errors.js'
import { execSetup, assertSafeTarball } from '../workspace.js'
import type { DownloadedSession } from './client.js'
import type { SessionMetadata } from './server.js'
import type { ProgressCallback } from '../session/manager.js'

export const DEFAULT_WORKER_URL = 'https://tonty-tech-interviews-relay.tonty-tech-interviews.workers.dev'

export function getWorkerUrl(): string {
  return process.env['TONTY_WORKER_URL'] || DEFAULT_WORKER_URL
}

export class CloudError extends TontyError {
  constructor(message: string) {
    super(message, 'CLOUD_ERROR', 'Check your network connection and try again')
  }
}

export async function uploadSession(
  workerUrl: string,
  metadata: SessionMetadata,
  systemPrompt: string,
  tarballPath: string,
  onProgress?: ProgressCallback,
): Promise<{ code: string; expiresAt: string }> {
  onProgress?.('Reading workspace tarball...')
  const tarballData = await readFile(tarballPath)

  onProgress?.('Uploading to cloud...')
  const formData = new FormData()
  formData.set('metadata', JSON.stringify(metadata))
  formData.set('systemPrompt', systemPrompt)
  formData.set(
    'workspace',
    new Blob([tarballData], { type: 'application/gzip' }),
    'workspace.tar.gz',
  )

  let response: Response
  try {
    response = await fetch(`${workerUrl}/sessions`, {
      method: 'POST',
      body: formData,
    })
  } catch (err) {
    throw new CloudError(
      `Failed to connect to cloud relay at ${workerUrl}: ${err instanceof Error ? err.message : String(err)}`,
    )
  }

  if (!response.ok) {
    const body = await response.text().catch(() => 'unknown error')
    throw new CloudError(`Cloud upload failed (${response.status}): ${body}`)
  }

  const result = (await response.json()) as { code: string; expiresAt: string }
  return result
}

export async function downloadSessionFromCloud(
  workerUrl: string,
  code: string,
  targetDir?: string,
  onProgress?: ProgressCallback,
): Promise<DownloadedSession> {
  const id = randomBytes(4).toString('hex')

  let rawCode = code.trim().toUpperCase()
  if (rawCode.startsWith('TONTY-')) {
    rawCode = rawCode.slice(6)
  }
  rawCode = rawCode.toLowerCase()

  onProgress?.('Connecting to cloud relay...')
  const metadataRes = await cloudFetch(`${workerUrl}/sessions/${rawCode}/metadata`)
  const metadata = (await metadataRes.json()) as SessionMetadata

  onProgress?.('Downloading scenario...')
  const promptRes = await cloudFetch(`${workerUrl}/sessions/${rawCode}/system-prompt`)
  const systemPrompt = await promptRes.text()

  onProgress?.('Downloading workspace...')
  const workspaceRes = await cloudFetch(`${workerUrl}/sessions/${rawCode}/workspace`)
  const tarball = Buffer.from(await workspaceRes.arrayBuffer())

  onProgress?.('Extracting workspace...')
  const workdir = targetDir ?? join(homedir(), 'tonty-sessions', `${metadata.scenarioName}-${id}`)
  await mkdir(workdir, { recursive: true })

  const tarballPath = join(workdir, '..', `${id}-download.tar.gz`)
  await writeFile(tarballPath, tarball)
  assertSafeTarball(tarballPath)
  execSync(`tar xzf "${tarballPath}" -C "${workdir}"`, { stdio: 'pipe' })
  try { execSync(`rm -f "${tarballPath}"`, { stdio: 'pipe' }) } catch { /* best-effort */ }

  // Always pull the scenario's current verify.sh from the relay and overwrite
  // any copy bundled in the tarball. The tarball may ship an older verify.sh
  // (e.g. GNU-only grep -Pzo, or --experimental-strip-types on Node 20), and
  // the clone-fallback path ships none at all — either way scoring would
  // false-FAIL a candidate who actually fixed the bug. The relay serves the
  // portable version (uploaded fresh from packages/scenarios), so joined
  // candidates are always scored against the current script.
  try {
    const verifyRes = await fetch(`${workerUrl}/sessions/${rawCode}/verify-sh`, {
      signal: AbortSignal.timeout(30000),
    })
    if (verifyRes.ok) {
      const verifyScript = await verifyRes.text()
      if (verifyScript.trim()) {
        await writeFile(join(workdir, 'verify.sh'), verifyScript)
        try { execSync('chmod +x verify.sh', { cwd: workdir, stdio: 'ignore' }) } catch { /* best-effort */ }
        onProgress?.('verify.sh ready')
      }
    }
  } catch {
    // Best-effort: fall back to whatever verify.sh came with the workspace
    // (tarball) — never fail the join because the verify fetch failed.
  }

  // Install dependencies. Templates strip node_modules to stay under the KV
  // size limit, and the clone-fallback path never ran setup — so both cases
  // replay setup here, matching the local `start` flow.
  if (metadata.setupCommands && metadata.setupCommands.length > 0) {
    onProgress?.('Installing dependencies...')
    for (const cmd of metadata.setupCommands) {
      const result = execSetup(cmd, workdir)
      if (!result.ok) {
        throw new SetupError(cmd, result.stderr)
      }
    }
  }

  const promptDir = join(homedir(), '.tonty-tech-interviews', 'prompts')
  await mkdir(promptDir, { recursive: true, mode: 0o700 })
  const systemPromptPath = join(promptDir, `${id}.md`)
  await writeFile(systemPromptPath, systemPrompt, { mode: 0o600 })

  return { workdir, systemPromptPath, metadata, id }
}

async function cloudFetch(url: string): Promise<Response> {
  let response: Response
  try {
    response = await fetch(url, { signal: AbortSignal.timeout(60000) })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new CloudError('Cloud download timed out')
    }
    throw new CloudError(
      `Failed to connect to cloud relay: ${err instanceof Error ? err.message : String(err)}`,
    )
  }

  if (response.status === 404) {
    throw new CloudError('Session not found or expired')
  }

  if (!response.ok) {
    const body = await response.text().catch(() => 'unknown error')
    throw new CloudError(`Cloud request failed (${response.status}): ${body}`)
  }

  return response
}
